import { Autocomplete, TextField, Typography } from "@mui/material";
import naicsCodes from "encamp-shared/src/constants/naics.json";
import { useValidatingFormContext } from "hooks/useValidatingForm";
import React, { forwardRef } from "react";
import { ControllerFieldState } from "react-hook-form";
import { DisplayOnlyField } from "./Forms/DisplayOnlyField";
import { ErrorDisplay } from "./Forms/ErrorDisplay";

type Props = {
  onChange: (naicsCode: number | null) => void;
  value: number | null;
  disabled?: boolean;
} & ControllerFieldState;

type NaicsCode = {
  code: number;
  description: string;
};

export const NaicsPicker: React.FC<Props> = forwardRef(function NaicsPicker(
  { onChange, value, error, disabled },
  ref
) {
  const autocompleteValue =
    naicsCodes.find((naicsCode) => naicsCode.code === value) ?? null;

  const getLabel = (naicsCode: NaicsCode | null) => {
    return naicsCode
      ? `${naicsCode?.description} (${naicsCode?.code})`.trim()
      : "";
  };

  const formContext = useValidatingFormContext();
  if (formContext?.metadata?.isDisplayOnly) {
    return (
      <DisplayOnlyField
        text={getLabel(autocompleteValue)}
        label={"NAICS Code"}
      />
    );
  }

  return (
    <>
      <Autocomplete
        ref={ref}
        options={naicsCodes}
        noOptionsText="Search for a NAICS Code"
        value={autocompleteValue}
        onChange={(_, val) => onChange(val?.code ?? null)}
        getOptionLabel={getLabel}
        renderOption={(props, naicsCode) => (
          <li {...props}>
            {naicsCode.description}&nbsp;
            <Typography color="gray">({naicsCode.code})</Typography>
          </li>
        )}
        disabled={disabled}
        renderInput={(params) => (
          <TextField
            {...params}
            error={!!error}
            label="NAICS Code"
            variant="outlined"
            autoComplete="off"
            inputProps={{
              ...params.inputProps,
              autoComplete: "off",
              "data-lpignore": "true",
              "data-form-type": "other",
            }}
            InputProps={{
              ...params.InputProps,
              endAdornment: <>{params.InputProps.endAdornment}</>,
            }}
          />
        )}
      />
      <ErrorDisplay error={error} />
    </>
  );
});
